import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { Check, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useSavedFilters } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

export type SpaceSection = 'tasks' | 'views' | 'docs' | 'projects'

interface SpaceSectionMenuProps {
  spaceId: string
  workspaceSlug: string
  spaceKey: string
  activeSection: SpaceSection
  onSectionChange: (section: SpaceSection) => void
}

/**
 * The title dropdown in a space's breadcrumb (design 26c): "All tasks ▾".
 * Replaces the tab row. It switches between the space's sections and its
 * saved views, so nothing the tabs offered is lost.
 */
export function SpaceSectionMenu({
  spaceId,
  workspaceSlug,
  spaceKey,
  activeSection,
  onSectionChange,
}: SpaceSectionMenuProps) {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const userId = useLoginStore((s) => s.userId)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { savedFilters } = useSavedFilters()

  const views = useMemo(
    () =>
      savedFilters
        .filter(
          (view) =>
            !view.isDeleted &&
            view.scope === 'space' &&
            view.scopeEntityId === spaceId &&
            (view.permission !== 'private' || view.ownerId === userId),
        )
        .sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.name.localeCompare(b.name)),
    [savedFilters, spaceId, userId],
  )

  const activeViewId = /\/views\/([^/]+)\/?$/.exec(pathname)?.[1] ?? null
  const activeView = views.find((view) => view.id === activeViewId)
  const sections: Array<SpaceSection> = ['tasks', 'docs', 'projects', 'views']
  const label = activeView ? activeView.name : t(`spaceSections.${activeSection}`)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex min-w-0 items-center gap-1 rounded-md px-1 py-0.5 font-medium text-foreground hover:bg-secondary">
        <span className="truncate">{label}</span>
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {sections.map((section) => (
          <DropdownMenuItem key={section} onSelect={() => onSectionChange(section)}>
            {t(`spaceSections.${section}`)}
            {!activeView && section === activeSection && <Check className="ml-auto size-3.5" />}
          </DropdownMenuItem>
        ))}
        {views.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              {t('spaceSections.savedViews')}
            </DropdownMenuLabel>
            {views.map((view) => (
              <DropdownMenuItem
                key={view.id}
                onSelect={() =>
                  void navigate({
                    to: '/$workspaceSlug/spaces/$spaceKey/views/$viewId',
                    params: { workspaceSlug, spaceKey, viewId: view.id },
                  })
                }
              >
                <span className="truncate">{view.name}</span>
                {view.id === activeViewId && <Check className="ml-auto size-3.5" />}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
